// Session status bucket/color/sort helpers — single source of truth for the
// playground session-list status marker (#1458). Exported as pure functions
// so they can be unit-tested without pulling React/V palette dependencies.
//
// Input vocabulary (what the API actually emits, grep "statusColor" in
// PlaygroundView.jsx): completed | active | failed | queued | created |
// stopped | timeout | unknown / missing.
//
// Output buckets (what the UI groups on): active | queued | failed |
// complete | stopped. `stopped` is its own bucket because user-aborted
// sessions are distinct from crashes (failed/timeout) and the Auto sort
// deliberately keeps them OUT of the top so the list focuses on
// in-flight + pending work.

export const STATUS_BUCKETS = ['active', 'queued', 'failed', 'complete', 'stopped'];

// Palette tokens — RESOLVED against the V palette at call site (keeps this
// module pure; callers pass `V` in or use STATUS_COLORS which already has
// concrete hex values matching V).
export const STATUS_COLORS = {
  active: '#007aff',    // V.blue — in-flight
  queued: '#6e6e73',    // V.textDim — waiting
  failed: '#ff3b30',    // V.red — something went wrong
  complete: '#d8d8dc',  // V.border — neutral, done
  stopped: '#ff9500',   // V.orange — user-aborted
};

export const STATUS_LABELS = {
  active: 'Active',
  queued: 'Queued',
  failed: 'Failed',
  complete: 'Complete',
  stopped: 'Stopped',
};

// Priority used by the `auto` sort: earlier = higher up in the list.
// Only `active` + `queued` float to the top (in-flight + pending work the
// user is waiting on). Every TERMINAL state — failed / complete / stopped —
// shares one bucket below, ordered purely by recency, so a failed session is
// NOT pinned above newer completed ones; it just sits in time order with them.
const BUCKET_PRIORITY = { active: 0, queued: 1, failed: 2, complete: 2, stopped: 2 };

// Raw → bucket. `stopped` = user-aborted, its own bucket (distinct from
// `failed` which is crash/error). `timeout` stays in `failed` — it's a
// system-side failure, surfaced the same way as `failed`. `created` =
// not yet running → queued.
export function getStatusBucket(rawStatus) {
  switch ((rawStatus || '').toLowerCase()) {
    case 'active':
    case 'running':
      return 'active';
    case 'queued':
    case 'created':
      return 'queued';
    case 'failed':
    case 'timeout':
    case 'error':
      return 'failed';
    case 'stopped':
      return 'stopped';
    case 'completed':
    case 'complete':
    case 'done':
    case 'finalized':
      return 'complete';
    default:
      return 'complete'; // safe default — unknown status renders as neutral
  }
}

// Timestamp extractor — sessions use `latestTs` (ms epoch) as the "last
// activity" field; fall back to `updatedAt` / `ts` if caller passes a
// different shape.
function getSessionTs(s) {
  return s?.latestTs ?? s?.updatedAt ?? s?.ts ?? 0;
}

// Auto sort: group by bucket priority, within group sort by timestamp desc.
export function sortSessionsAuto(sessions) {
  return [...(sessions || [])].sort((a, b) => {
    const pa = BUCKET_PRIORITY[getStatusBucket(a.status)];
    const pb = BUCKET_PRIORITY[getStatusBucket(b.status)];
    if (pa !== pb) return pa - pb;
    return getSessionTs(b) - getSessionTs(a);
  });
}

// Chronological sort (latest first) — used by any non-`auto` filter.
export function sortSessionsByTsDesc(sessions) {
  return [...(sessions || [])].sort((a, b) => getSessionTs(b) - getSessionTs(a));
}

// Filter + sort. Filter values: 'auto' | 'active' | 'queued' | 'failed' |
// 'complete' | 'all'. 'auto' = no filter + grouped sort. 'all' = no filter +
// chronological sort. Bucket names = filter to that bucket + chronological.
export function filterSessions(sessions, filter) {
  const f = filter || 'auto';
  if (f === 'auto') return sortSessionsAuto(sessions);
  if (f === 'all') return sortSessionsByTsDesc(sessions);
  if (STATUS_BUCKETS.includes(f)) {
    const filtered = (sessions || []).filter(s => getStatusBucket(s.status) === f);
    return sortSessionsByTsDesc(filtered);
  }
  // unknown filter → fall back to auto rather than returning empty
  return sortSessionsAuto(sessions);
}

export const STATUS_FILTER_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'active', label: 'Active' },
  { value: 'queued', label: 'Queued' },
  { value: 'failed', label: 'Failed' },
  { value: 'complete', label: 'Complete' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'all', label: 'All' },
];
